import {
  Box,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import React from "react";
import QuizIcon from "../../../../../assets/icons/quiz-icon.svg";
import { Share } from "@mui/icons-material";
import {
  useGetQuizzesPaginatedQuery,
  useGetQuizResultQuery,
} from "../../../../../app/quizApiSlice";
import CustomInfoCard from "../../../../../customComponents/CustomInfoCard";
import { useNavigate } from "react-router-dom";
import CustomResultModal from "../../../../../customComponents/CustomResultModal";
import { parseUTC } from "../../../../../helpers/dateUtils";

const ViewQuizzes = () => {
  const [sortBy, setSortBy] = React.useState("date");
  const [openModals, setOpenModals] = React.useState({});
  const navigate = useNavigate();
  const { data, isLoading, isFetching } = useGetQuizzesPaginatedQuery({
    page,
    per_page: itemsPerPage,
    sort_by: sortBy,
    is_assigned: true,
  });
  const [selectedQuizId, setSelectedQuizId] = React.useState(null);
  const [correctQuiz, setCorrectQuiz] = React.useState(false);
  const { data: quizResult, isError } = useGetQuizResultQuery(
    { quiz_id: selectedQuizId || 0 },
    { skip: !selectedQuizId }
  );

  const [page, setPage] = React.useState(1);
  const itemsPerPage = 10;

  const formatDate = (dateString) => {
    const date = parseUTC(dateString);

    if (!date) {
      throw new Error("Invalid date string");
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const quizzes = data?.quizzes || [];

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeSort = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const totalQuizzes = data?.pagination?.total || 0;
  const totalPages = data?.pagination?.pages || 0;
  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalQuizzes);

  const handleOpenModal = (quizId) => {
    setSelectedQuizId(quizId);
    setOpenModals((prev) => ({ ...prev, [quizId]: true }));
    if (quizResult) {
      const quizIds = quizResult.answers?.map((answer) => answer.quiz_id) || [];
      setCorrectQuiz(quizIds.includes(quizId));
    }
  };

  const handleCloseModal = (quizId) => {
    setOpenModals((prev) => ({ ...prev, [quizId]: false }));
    setSelectedQuizId(null);
  };

  React.useEffect(() => {
    if (quizResult && selectedQuizId !== null) {
      const quizIds = quizResult.answers?.map((answer) => answer.quiz_id) || [];
      setCorrectQuiz(quizIds.includes(selectedQuizId));
      console.log(quizResult);
    }
  }, [quizResult, selectedQuizId]);

  const result = {
    correct: 0,
    wrong: 0,
  };

  quizResult?.answers?.forEach((answer) => {
    const correctOption = answer.question.options.find(
      (option) => option.id === answer.option_id && option.is_answer === true
    );

    if (correctOption) {
      result.correct += 1;
    } else {
      result.wrong += 1;
    }
  });

  const totalQuestions = quizResult?.answers?.length || 0;
  const percentage = (result.correct / totalQuestions) * 100;

  return (
    <Box>
      <Box sx={{ marginTop: "2em" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1em",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontWeight: 700,
              letterSpacing: "-1px",
            }}
          >
            Class Quizzes
          </Typography>
          <Select value={sortBy} onChange={handleChangeSort} size="small">
            <MenuItem value="date">
              Sort by: <strong>Date</strong>
            </MenuItem>
            <MenuItem value="subject">
              Sort by: <strong>Subject</strong>
            </MenuItem>
          </Select>
        </Box>

        {(isLoading || isFetching) && <LinearProgress />}

        {quizzes.map((quiz) => (
          <Box key={quiz.id}>
            <CustomInfoCard
              title={quiz.subject}
              date={formatDate(quiz.updated_at)}
              details={quiz.questions[0]?.topic}
              handleClick={() => {
                if (quiz.answers) {
                  handleOpenModal(quiz.id);
                  console.log(quiz.answers);
                } else {
                  navigate(`view/${quiz.id}`, {
                    state: { quiz },
                  });
                }
              }}
              icon={QuizIcon}
              action={"View"}
              logo={<Share />}
            />
            <CustomResultModal
              open={openModals[quiz.id] || false}
              handleClose={() => handleCloseModal(quiz.id)}
              score={percentage.toFixed(1)}
              totalQuestions={totalQuestions}
              correctAnswers={result.correct}
              wrongAnswers={result.wrong}
              handleViewSolutions={() => {
                navigate(`view/${quiz.id}`, {
                  state: { quiz },
                });
              }}
              isLoading={!correctQuiz && !isError}
              isError={isError}
            />
          </Box>
        ))}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "2em",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {startIndex} to {endIndex} of {totalQuizzes} entries
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
            sx={{ color: (theme) => theme.palette.mode === "dark" ? "#888" : "#CCCCCC" }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ViewQuizzes;
